'use client'
import type { PropsWithStyles } from '@morpho-org/ui-v6/types'

import { cn } from '@morpho-org/ui-v6/utils'
import { Button } from '@repo/ui'
import { useRouter } from 'next/navigation'
import { type CSSProperties, startTransition } from 'react'

import { ButtonLink } from '@/components/common/ButtonLink'
import { useSectionErrorBoundaryReset } from '@/components/common/SectionErrorBoundary'
import { clientEnvs } from '@/config/client-envs'

export interface ErrorDataUnavailableProps extends PropsWithStyles {
  minHeight?: CSSProperties['height']
  title?: string
  description?: string
  showActions?: boolean
}

export default function ErrorDataUnavailable({
  minHeight = '250px',
  title = 'Something went wrong.',
  description = 'This could be due to connectivity or other local issues.',
  showActions = true,
  className,
  style
}: ErrorDataUnavailableProps) {
  const router = useRouter()
  const resetSection = useSectionErrorBoundaryReset()

  return (
    <div
      className={cn(
        'flex gap-md flex-col items-center justify-center p-md',
        cn('w-full rounded-[20px] bg-card', className)
      )}
      style={{ minHeight, ...style }}
    >
      <div className="gap-sm flex flex-col">
        <span className="text-sm" style={{ textAlign: 'center' }}>
          {title}
        </span>
        <span
          className="text-2xs text-secondary-foreground"
          style={{ maxWidth: '450px', textAlign: 'center' }}
        >
          {description}
        </span>
      </div>
      {showActions && (
        <div className="gap-sm flex">
          <Button
            size="sm"
            onClick={() =>
              startTransition(() => {
                router.refresh()
                resetSection?.()
              })
            }
          >
            Refresh App
          </Button>
          <ButtonLink
            mode="external"
            href={clientEnvs.NEXT_PUBLIC_STATUS_URL}
            buttonProps={{
              size: 'sm',
              variant: 'secondary',
              label: 'Check App Status'
            }}
          />
        </div>
      )}
    </div>
  )
}
